import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react'

import { cn } from './cn'

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  error?: string
  hint?: string
  leftAddon?: ReactNode
  rightAddon?: ReactNode
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, error, hint, leftAddon, rightAddon, className, id, ...rest },
  ref,
) {
  const reactId = useId()
  const inputId = id ?? `pc-input-${reactId}`
  const describedBy = error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined

  return (
    <div className="flex flex-col gap-1">
      {label ? (
        <label htmlFor={inputId} className="text-xs font-medium text-neutral-300">
          {label}
        </label>
      ) : null}
      <div
        className={cn(
          'flex items-center gap-2 rounded-md border border-white/10 bg-black/20 px-3 transition',
          'focus-within:border-pharmacist focus-within:ring-1 focus-within:ring-pharmacist',
          error && 'border-offline focus-within:border-offline focus-within:ring-offline',
        )}
      >
        {leftAddon ? <span className="text-neutral-400">{leftAddon}</span> : null}
        <input
          ref={ref}
          id={inputId}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={cn(
            'h-10 flex-1 bg-transparent text-sm text-white placeholder:text-neutral-500 focus:outline-none',
            'disabled:cursor-not-allowed disabled:opacity-50',
            className,
          )}
          {...rest}
        />
        {rightAddon ? <span className="text-neutral-400">{rightAddon}</span> : null}
      </div>
      {error ? (
        <p id={`${inputId}-error`} className="text-xs text-offline">
          {error}
        </p>
      ) : hint ? (
        <p id={`${inputId}-hint`} className="text-xs text-neutral-400">
          {hint}
        </p>
      ) : null}
    </div>
  )
})
